import React, { useMemo, useCallback } from 'react';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';
import GradientEditor, { MapLibreHeatmapColorExpression } from '../../../../common/custom-editors/GradientEditor';
import type { LayerBase, LayerStyleProperties, Expression } from '../../../../store/types';

// Definiamo un tipo più specifico per i layer heatmap se necessario,
// altrimenti facciamo affidamento su LayerBase e cast.
// Per ora, assumiamo che le proprietà necessarie siano accessibili tramite LayerBase
// o che LayerStyleProperties sia flessibile.
interface HeatmapLayer extends LayerBase, LayerStyleProperties {
  maplibreLayerType: 'heatmap';
  layout?: {
    visibility?: 'visible' | 'none';
    // Altre proprietà di layout specifiche per heatmap qui
  };
  paint?: {
    'heatmap-color'?: MapLibreHeatmapColorExpression | string; // Aggiornato per riflettere che può essere un'espressione o un colore semplice
    'heatmap-intensity'?: StyleValue; // Numero o espressione
    'heatmap-opacity'?: StyleValue; // Numero o espressione
    'heatmap-radius'?: StyleValue; // Numero o espressione
    'heatmap-weight'?: StyleValue; // Numero o espressione
    // Altre proprietà di paint specifiche per heatmap qui
  };
}

interface StyleEditorSectionProps {
  layer: HeatmapLayer; // Usiamo il tipo più specifico o LayerBase e facciamo cast
  updateLayerStyle: ( // Modificato per corrispondere a GeoDataState['updateLayerStyleProperty']
    layerId: string,
    propertyName: keyof LayerStyleProperties,
    value: Expression | undefined
  ) => void;
  isDisabled?: boolean;
  availablePropertyKeys?: string[]; // Aggiunto per coerenza con StylePropertyInput
}

export const HeatmapLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled, availablePropertyKeys }) => {
  if (layer.maplibreLayerType !== 'heatmap') {
    return null;
  }

  return (
    <StylePropertyInput
      key={`${layer.id}-layout-visibility`} // Chiave più specifica
      layerId={layer.id}
      propertyName="visibility" // Proprietà di primo livello, gestita da updateLayerStyle
      label="Visibilità"
      currentValue={layer.layout?.visibility || 'visible'}
      propertyType="select"
      selectOptions={[
        { label: 'Visibile', value: 'visible' },
        { label: 'Nascosto', value: 'none' },
      ]}
      // La callback onStyleChange di StylePropertyInput si aspetta (layerId, propertyName: keyof LayerStyleProperties, value)
      // La nostra updateLayerStyle accetta (layerId, propertyName: string, value)
      // Quindi, dobbiamo assicurarci che "visibility" sia un keyof LayerStyleProperties valido
      // o che StylePropertyInput sia flessibile, oppure fare un cast.
      // Assumendo che "visibility" sia in LayerStyleProperties (come in FillStyleEditor):
      onStyleChange={(id, propName, val) => updateLayerStyle(id, propName as keyof LayerStyleProperties, val as Expression | undefined)}
      availablePropertyKeys={availablePropertyKeys} // Passato da props
      isExpressionPossible={true} // Generalmente vero per le proprietà di visibilità
      disabled={isDisabled}
    />
  );
};

export const HeatmapPaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled, availablePropertyKeys }) => {
  if (layer.maplibreLayerType !== 'heatmap') {
    return null;
  }

  const defaultHeatmapColorExpression = useMemo(() =>
    [
      "interpolate", ["linear"], ["heatmap-density"],
      0, "rgba(0, 0, 255, 0)",
      0.1, "royalblue",
      0.3, "cyan",
      0.5, "lime",
      0.7, "yellow",
      1, "red"
    ] as MapLibreHeatmapColorExpression,
  []);

  const currentHeatmapColor = layer.paint?.['heatmap-color'];
  const actualInitialExpression = Array.isArray(currentHeatmapColor)
    ? currentHeatmapColor
    : defaultHeatmapColorExpression;

  const handleGradientChange = useCallback((newExpression: MapLibreHeatmapColorExpression) => {
    updateLayerStyle(layer.id, "heatmapColor" as keyof LayerStyleProperties, newExpression as Expression);
  }, [updateLayerStyle, layer.id]);

  return (
    <>
      <GradientEditor
        initialExpression={actualInitialExpression}
        onChange={handleGradientChange}
        isDisabled={isDisabled}
      />
      <StylePropertyInput
        key={`${layer.id}-heatmap-intensity`}
        layerId={layer.id}
        propertyName="heatmapIntensity"
        label="Intensità Heatmap"
        currentValue={layer.paint?.['heatmap-intensity']}
        propertyType="range" // o "number"
        numberConfig={{ min: 0, step: 0.1, max: 10 }} // max suggerito
        onStyleChange={(id, propName, val) => updateLayerStyle(id, propName as keyof LayerStyleProperties, val as Expression | undefined)}
        availablePropertyKeys={availablePropertyKeys}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      <StylePropertyInput
        key={`${layer.id}-heatmap-opacity`}
        layerId={layer.id}
        propertyName="heatmapOpacity"
        label="Opacità Heatmap"
        currentValue={layer.paint?.['heatmap-opacity']}
        propertyType="range"
        numberConfig={{ min: 0, max: 1, step: 0.01 }}
        onStyleChange={(id, propName, val) => updateLayerStyle(id, propName as keyof LayerStyleProperties, val as Expression | undefined)}
        availablePropertyKeys={availablePropertyKeys}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      <StylePropertyInput
        key={`${layer.id}-heatmap-radius`}
        layerId={layer.id}
        propertyName="heatmapRadius"
        label="Raggio Heatmap (px)"
        currentValue={layer.paint?.['heatmap-radius']}
        propertyType="number" // o "range"
        numberConfig={{ min: 0, step: 1 }}
        onStyleChange={(id, propName, val) => updateLayerStyle(id, propName as keyof LayerStyleProperties, val as Expression | undefined)}
        availablePropertyKeys={availablePropertyKeys}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      <StylePropertyInput
        key={`${layer.id}-heatmap-weight`}
        layerId={layer.id}
        propertyName="heatmapWeight"
        label="Peso Heatmap"
        currentValue={layer.paint?.['heatmap-weight']}
        propertyType="number" // o "range"
        numberConfig={{ min: 0, step: 0.1 }}
        onStyleChange={(id, propName, val) => updateLayerStyle(id, propName as keyof LayerStyleProperties, val as Expression | undefined)}
        availablePropertyKeys={availablePropertyKeys}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
    </>
  );
};

// Per HeatmapStyleEditor, le props sono definite ma non usate nel corpo perché è un placeholder.
// Per evitare l'errore "All destructured elements are unused", non destrutturiamo o usiamo _props.
const HeatmapStyleEditor: React.FC<StyleEditorSectionProps> = (_props) => {
  // Questo componente è inteso per assemblare HeatmapLayoutProperties e HeatmapPaintProperties
  // e riceverà le props definite in StyleEditorSectionProps.
  // Attualmente restituisce null come da richiesta.
  // Esempio di come potrebbe essere usato:
  // return (
  //   <>
  //     <HeatmapLayoutProperties {..._props} />
  //     <HeatmapPaintProperties {..._props} />
  //   </>
  // );
  return null;
};

export default HeatmapStyleEditor;